import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Check } from 'lucide-react';
import Modal from '../Modal';
import { reminderApi, CreateReminderDto, ReminderType, ReminderPriority } from '../../services/reminderService';
import { clientsApi, ClientDto } from '../../services/clientsService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const ErrorText = styled.span`
  color: var(--error-color);
  font-size: 13px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface AddReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultUserId: number;
  preSelectedClientId?: number;
}

const AddReminderModal: React.FC<AddReminderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultUserId,
  preSelectedClientId
}) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateReminderDto>({
    userId: defaultUserId,
    reminderType: ReminderType.FollowUp,
    title: '',
    description: '',
    dueDateTime: '',
    entityType: 2, // Client
    entityId: preSelectedClientId,
    priority: ReminderPriority.Medium,
    isRecurring: false,
    recurrencePattern: undefined
  });

  useEffect(() => {
    if (isOpen) {
      loadClients();
      // Set default date to tomorrow at 9:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      const dateString = tomorrow.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, dueDateTime: dateString }));
    }
  }, [isOpen]);

  const loadClients = async () => {
    try {
      const result = await clientsApi.getAll();
      setClients(result.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'כותרת היא שדה חובה';
    }

    if (!formData.dueDateTime) {
      newErrors.dueDateTime = 'תאריך ושעה הם שדות חובה';
    } else {
      const selectedDate = new Date(formData.dueDateTime);
      if (selectedDate <= new Date()) {
        newErrors.dueDateTime = 'תאריך היעד חייב להיות בעתיד';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      await reminderApi.createReminder(formData);
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data || 'שגיאה ביצירת התזכורת');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateReminderDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="הוספת תזכורת חדשה"
      width="600px"
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            סוג התזכורת
          </Label>
          <Select
            value={formData.reminderType}
            onChange={(e) => handleChange('reminderType', parseInt(e.target.value))}
          >
            <option value={ReminderType.Call}>שיחה</option>
            <option value={ReminderType.FollowUp}>מעקב</option>
            <option value={ReminderType.Document}>מסמך</option>
            <option value={ReminderType.Meeting}>פגישה</option>
            <option value={ReminderType.Payment}>תשלום</option>
            <option value={ReminderType.Custom}>אחר</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            כותרת
          </Label>
          <Input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="למשל: להתקשר ללקוח לגבי תוצאות בדיקה"
            hasError={!!errors.title}
          />
          {errors.title && <ErrorText>{errors.title}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>תיאור</Label>
          <TextArea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="פרטים נוספים על התזכורת..."
          />
        </FormGroup>

        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            תאריך ושעת היעד
          </Label>
          <Input
            type="datetime-local"
            value={formData.dueDateTime}
            onChange={(e) => handleChange('dueDateTime', e.target.value)}
            hasError={!!errors.dueDateTime}
          />
          {errors.dueDateTime && <ErrorText>{errors.dueDateTime}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            עדיפות
          </Label>
          <Select
            value={formData.priority}
            onChange={(e) => handleChange('priority', parseInt(e.target.value))}
          >
            <option value={ReminderPriority.High}>גבוהה - דחוף</option>
            <option value={ReminderPriority.Medium}>בינונית</option>
            <option value={ReminderPriority.Low}>נמוכה</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>קשור ללקוח (אופציונלי)</Label>
          <Select
            value={formData.entityId || ''}
            onChange={(e) => handleChange('entityId', e.target.value ? parseInt(e.target.value) : undefined)}
          >
            <option value="">לא קשור ללקוח ספציפי</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.fullName} - {client.idNumber}
              </option>
            ))}
          </Select>
        </FormGroup>

        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            ביטול
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'שומר...' : <ButtonContent><Check size={16} strokeWidth={2} />צור תזכורת</ButtonContent>}
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default AddReminderModal;
